import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const environmentalModule: ChecklistModuleDefinition = {
  code: 'environmental',
  name: 'Environmental',
  description: 'Environmental assessments, contamination, protected species',
  categories: [
    {
      code: 'contamination',
      name: 'Contamination Assessment',
      items: [
        {
          code: 'historical_use',
          title: 'Historical use review',
          description: 'Research past property uses',
          isGroupTask: false,
          dataFields: [
            { key: 'historical_uses', label: 'Historical Uses', type: 'array' },
            { key: 'concerning_uses', label: 'Concerning Uses Identified', type: 'boolean' },
            { key: 'concerns', label: 'Specific Concerns', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Industrial/contamination history' },
            { flag: 'red', condition: 'Gas station, dry cleaner, or similar use' },
            { flag: 'yellow', condition: 'Unknown historical use' },
          ],
        },
        {
          code: 'phase1_esa',
          title: 'Phase I ESA assessment',
          description: 'Environmental Site Assessment review',
          isGroupTask: false,
          dataFields: [
            { key: 'phase1_done', label: 'Phase I Completed', type: 'boolean' },
            { key: 'report_date', label: 'Report Date', type: 'date' },
            { key: 'findings', label: 'Key Findings', type: 'array' },
            { key: 'recs_recognized', label: 'RECs Recognized', type: 'boolean' },
            { key: 'phase2_recommended', label: 'Phase II Recommended', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'RECs identified, Phase II needed' },
            { flag: 'red', condition: 'Environmental concerns found' },
            { flag: 'green', condition: 'Clean Phase I' },
          ],
        },
        {
          code: 'underground_tanks',
          title: 'Underground storage tanks',
          description: 'Check for USTs',
          isGroupTask: false,
          dataFields: [
            { key: 'ust_present', label: 'USTs Present', type: 'boolean' },
            { key: 'ust_registered', label: 'Tanks Registered', type: 'boolean' },
            { key: 'tank_status', label: 'Tank Status', type: 'select', options: [
              { value: 'active', label: 'Active' },
              { value: 'closed', label: 'Properly Closed' },
              { value: 'abandoned', label: 'Abandoned' },
              { value: 'unknown', label: 'Unknown' },
            ]},
            { key: 'contents', label: 'Contents', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Abandoned USTs' },
            { flag: 'red', condition: 'USTs present' },
          ],
        },
        {
          code: 'dumping_debris',
          title: 'Dumping/debris assessment',
          description: 'Check for illegal dumping',
          isGroupTask: false,
          dataFields: [
            { key: 'dumping_found', label: 'Dumping Found', type: 'boolean' },
            { key: 'debris_types', label: 'Debris Types', type: 'array' },
            { key: 'cleanup_cost', label: 'Est. Cleanup Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Hazardous waste dumping' },
            { flag: 'red', condition: 'Significant debris requiring cleanup' },
            { flag: 'yellow', condition: 'Minor debris present' },
          ],
        },
        {
          code: 'adjacent_contamination',
          title: 'Adjacent property concerns',
          description: 'Neighboring property environmental issues',
          isGroupTask: false,
          dataFields: [
            { key: 'concerns_identified', label: 'Concerns Identified', type: 'boolean' },
            { key: 'adjacent_uses', label: 'Adjacent Uses', type: 'array' },
            { key: 'migration_risk', label: 'Migration Risk', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Adjacent contamination risk' },
          ],
        },
      ],
    },
    {
      code: 'protected_species',
      name: 'Protected Species',
      items: [
        {
          code: 'endangered_species',
          title: 'Endangered species check',
          description: 'Check for protected species/habitat',
          isGroupTask: false,
          dataFields: [
            { key: 'species_present', label: 'Protected Species Present', type: 'boolean' },
            { key: 'species_list', label: 'Species', type: 'array' },
            { key: 'critical_habitat', label: 'Critical Habitat', type: 'boolean' },
            { key: 'restrictions', label: 'Activity Restrictions', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Critical habitat, severe restrictions' },
            { flag: 'red', condition: 'Endangered species present' },
            { flag: 'yellow', condition: 'Potential habitat, needs survey' },
          ],
        },
        {
          code: 'protected_habitat',
          title: 'Protected habitat areas',
          description: 'Sensitive ecological areas',
          isGroupTask: false,
          dataFields: [
            { key: 'habitat_types', label: 'Protected Habitat Types', type: 'array' },
            { key: 'acres_affected', label: 'Acres Affected', type: 'number', unit: 'acres' },
            { key: 'management_requirements', label: 'Management Requirements', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'regulatory',
      name: 'Environmental Regulations',
      items: [
        {
          code: 'permits_required',
          title: 'Environmental permits required',
          description: 'Permits needed for intended use',
          isGroupTask: false,
          dataFields: [
            { key: 'permits_needed', label: 'Permits Needed', type: 'array' },
            { key: 'permit_difficulty', label: 'Permit Difficulty', type: 'select', options: [
              { value: 'routine', label: 'Routine' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'difficult', label: 'Difficult' },
              { value: 'unlikely', label: 'Unlikely to Obtain' },
            ]},
            { key: 'permit_costs', label: 'Est. Permit Costs', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Difficult permitting required' },
          ],
        },
        {
          code: 'state_regulations',
          title: 'State environmental regulations',
          description: 'State-specific requirements',
          isGroupTask: false,
          dataFields: [
            { key: 'regulations_apply', label: 'State Regulations Apply', type: 'boolean' },
            { key: 'regulation_types', label: 'Regulation Types', type: 'array' },
            { key: 'compliance_status', label: 'Compliance Status', type: 'string' },
          ],
        },
        {
          code: 'federal_regulations',
          title: 'Federal environmental regulations',
          description: 'Federal requirements (NEPA, etc.)',
          isGroupTask: false,
          dataFields: [
            { key: 'federal_regs_apply', label: 'Federal Regulations Apply', type: 'boolean' },
            { key: 'regulation_types', label: 'Regulation Types', type: 'array' },
          ],
        },
      ],
    },
    {
      code: 'environmental_assets',
      name: 'Environmental Assets',
      items: [
        {
          code: 'carbon_credits',
          title: 'Carbon credit potential',
          description: 'Carbon sequestration opportunities',
          isGroupTask: false,
          dataFields: [
            { key: 'carbon_potential', label: 'Carbon Credit Potential', type: 'boolean' },
            { key: 'eligible_acres', label: 'Eligible Acres', type: 'number', unit: 'acres' },
            { key: 'estimated_value', label: 'Estimated Value', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Significant carbon credit potential' },
          ],
        },
        {
          code: 'conservation_programs',
          title: 'Conservation program eligibility',
          description: 'NRCS and other program eligibility',
          isGroupTask: false,
          dataFields: [
            { key: 'programs_available', label: 'Programs Available', type: 'array' },
            { key: 'potential_funding', label: 'Potential Funding', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
  ],
};

